import { useEffect, useRef, useState } from "react";
import { Row, Col, Carousel } from "react-bootstrap";

const LazyVideo = ({ src }) => {
  const containerRef = useRef(null);
  const [isNearViewport, setIsNearViewport] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setIsNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="proj-card__video-wrapper">
      {isNearViewport && (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          className="proj-card__video"
        >
          <source src={src} type="video/webm" />
        </video>
      )}
    </div>
  );
};

const ProjectMediaItem = ({ media, title }) => {
  if (media.type === "video") {
    return <LazyVideo src={media.src} />;
  }

  return (
    <img
      src={media.src}
      alt={`${title} preview`}
      width={media.width}
      height={media.height}
      className="proj-card__img"
      loading="lazy"
      decoding="async"
      fetchPriority="low"
    />
  );
};

export const ProjectCard = ({ project, index }) => {
  const revealRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const isReversed = index % 2 !== 0;
  const hasCarousel = project.images.length > 1;

  useEffect(() => {
    const el = revealRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -6% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const mediaEl = (
    <div className="proj-card__media">
      <div className="proj-card__media-inner">
        {hasCarousel ? (
          <Carousel
            className="proj-card__carousel"
            controls
            indicators
            interval={null}
            touch
            variant="dark"
            prevIcon={
              <span
                className="proj-card__carousel-arrow proj-card__carousel-arrow--prev"
                aria-hidden="true"
              />
            }
            nextIcon={
              <span
                className="proj-card__carousel-arrow proj-card__carousel-arrow--next"
                aria-hidden="true"
              />
            }
          >
            {project.images.map((media, mediaIndex) => (
              <Carousel.Item key={`${project.id}-${mediaIndex}`}>
                <ProjectMediaItem media={media} title={project.title} />
              </Carousel.Item>
            ))}
          </Carousel>
        ) : (
          <ProjectMediaItem media={project.images[0]} title={project.title} />
        )}
      </div>
    </div>
  );

  const contentEl = (
    <div className="proj-card__content">
      {project.stack && (
        <ul className="proj-card__stack" aria-label="Technologies used">
          {project.stack.map((tag) => (
            <li key={tag} className="proj-card__tag">{tag}</li>
          ))}
        </ul>
      )}
      <h3 className="proj-card__title">{project.title}</h3>
      <p className="proj-card__desc">{project.description}</p>
      {project.user && (
        <p className="proj-card__demo">{project.user}</p>
      )}
      <div className="proj-card__actions">
        {project.live ? (
          <a
            className="proj-card__btn proj-card__btn--primary"
            href={project.live}
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit Live
          </a>
        ) : (
          <span
            className="proj-card__btn proj-card__btn--disabled"
            aria-disabled="true"
          >
            Visit Live
          </span>
        )}
        <a
          className="proj-card__btn proj-card__btn--secondary"
          href={project.repository}
          target="_blank"
          rel="noopener noreferrer"
        >
          Source Code
        </a>
      </div>
    </div>
  );

  return (
    <article
      className={`proj-card${isVisible ? " is-visible" : ""}`}
      ref={revealRef}
      style={{ "--reveal-delay": `${index * 0.1}s` /* was `${index * 0.15}s` */ }}
    >
      <Row
        className={`proj-card__inner g-0 align-items-center${
          isReversed ? " proj-card__inner--reversed" : ""
        }`}
      >
        {isReversed ? (
          <>
            <Col md={6} className="proj-card__media-col">
              {mediaEl}
            </Col>
            <Col md={6} className="proj-card__content-col">
              {contentEl}
            </Col>
          </>
        ) : (
          <>
            <Col md={6} className="proj-card__content-col">
              {contentEl}
            </Col>
            <Col md={6} className="proj-card__media-col">
              {mediaEl}
            </Col>
          </>
        )}
      </Row>
    </article>
  );
};
